import { Component, EventEmitter, Output, inject } from '@angular/core';
import { SecondaryButtonComponent } from '../../../../../shared/components/secondary-button/secondary-button.component';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { HashMap, TranslocoDirective } from '@jsverse/transloco';
import { ButtonRowComponent } from '../../../../../shared/components/button-row/button-row.component';
import { UserService } from '../../services/user.service';
import {
  emailValidationPattern,
  forbiddenCharactersValidator,
} from '../../../../../shared/validators/custom-validators';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TextInputComponent } from '../../../../../shared/components/text-input/text-input.component';
import { transformUserFormDataToUserDTO } from '../../mappers/dto-mappers';
import { SnackbarService } from '../../../../../core/services/snackbar.service';
import { CustomPortalError } from '../../../../../core/errors/models/CustomPortalError';
import { AnalyticsService } from '../../../../../core/services/analytics/analytics.service';
import { HrisIntegrationButtonComponent } from '../../../organization/components/hris-integration-button/hris-integration-button.component';
import { FeatureFlagsService } from '../../../../../core/services/feature-flag.service';
import { MatIcon } from '@angular/material/icon';
import { LinkComponent } from '../../../../../shared/components/link/link.component';
import { SlideToggleComponent } from '../../../../../shared/components/slide-toggle/slide-toggle.component';
import { ErrorType } from '../../../../../core/errors/models/ErrorType';
import {
  catchError,
  finalize,
  Observable,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { LicenseCalculationsService } from '../../../../payment/subscription/services/license-calculations.service';

export interface UserFormControls {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  isAdmin: FormControl<boolean>;
}

@Component({
  selector: 'app-user-creation-container',
  imports: [
    ReactiveFormsModule,
    SecondaryButtonComponent,
    TextInputComponent,
    TranslocoDirective,
    ButtonRowComponent,
    MatDividerModule,
    MatTooltipModule,
    SecondaryButtonComponent,
    HrisIntegrationButtonComponent,
    MatIcon,
    LinkComponent,
    SlideToggleComponent,
  ],
  templateUrl: './user-creation-container.component.html',
  styleUrl: './user-creation-container.component.css',
})
export class UserCreationContainerComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly userService = inject(UserService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly licenseCalculationService = inject(
    LicenseCalculationsService,
  );

  @Output() backButtonClickEvent: EventEmitter<void> = new EventEmitter<void>();
  @Output() userSuccessfullyCreatedEvent: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() csvUploadButtonClickEvent: EventEmitter<void> =
    new EventEmitter<void>();

  isLoading = false;

  userCreationForm: FormGroup<UserFormControls> =
    this.formBuilder.group<UserFormControls>({
      firstName: this.formBuilder.nonNullable.control(
        { value: '', disabled: false },
        [
          Validators.required,
          Validators.maxLength(50),
          forbiddenCharactersValidator(),
        ],
      ),
      lastName: this.formBuilder.nonNullable.control(
        { value: '', disabled: false },
        [
          Validators.required,
          Validators.maxLength(50),
          forbiddenCharactersValidator(),
        ],
      ),
      email: this.formBuilder.nonNullable.control(
        { value: '', disabled: false },
        [
          Validators.required,
          Validators.pattern(emailValidationPattern),
          forbiddenCharactersValidator(),
        ],
      ),
      isAdmin: this.formBuilder.nonNullable.control({
        value: false,
        disabled: false,
      }),
    });

  handleUserCreationSubmit() {
    this.createUser();
  }

  private createUser() {
    this.isLoading = true;
    this.userCreationForm.disable();
    const users = [transformUserFormDataToUserDTO(this.userCreationForm.value)];
    this.userService
      .createUsers(users)
      .pipe(
        // see `hris-integration-data.service.ts:sync()` for a more detailed explanation why it is handled this way
        catchError((error: CustomPortalError) => this.handleCreateError(error)),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.analyticsService.track('user_created');
          this.userSuccessfullyCreatedEvent.emit();

          this.snackbarService.success(
            'portal.users.creation.successfulCreationSnackbar',
            { count: users.length },
          );
        },
      });
  }

  private handleCreateError(error: CustomPortalError): Observable<never> {
    const handleError = (params?: HashMap) => {
      this.snackbarService.error(error.errorType, true, params);
    };

    const handled$ = throwError(() => undefined);

    if (error.errorType === ErrorType.NOT_ENOUGH_LICENSES_AVAILABLE) {
      return this.licenseCalculationService
        .getValidAmountOfUsersToLicense()
        .pipe(
          tap((validAmount) => {
            handleError({ allowedNumberOfUsers: validAmount });
          }),
          switchMap(() => handled$),
        );
    }

    handleError();

    return handled$;
  }

  handleBackButtonClick() {
    this.backButtonClickEvent.emit();
  }

  handleCsvButtonClick() {
    this.analyticsService.track('csv_upload_clicked');
    this.csvUploadButtonClickEvent.emit();
  }

  isHRISEnabled() {
    return this.featureFlagsService.isFeatureEnabled(
      this.featureFlagsService.hris,
    );
  }
}
